// src/components/Signup/PasswordStep.jsx

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { BiCheck, BiX, BiShow, BiHide } from 'react-icons/bi';
import Button from '../Button';
import { validatePassword, calculatePasswordStrength } from '../../lib/validationUtils';

const PasswordStep = ({ formData, onFormDataChange, onNext, onBack }) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [hasErrors, setHasErrors] = useState(false);

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    onFormDataChange(updatedData);
    
    // Clear errors when user starts typing
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
      setHasErrors(Object.keys(newErrors).length > 0);
    }

    // Calculate password strength in real-time
    if (field === 'password') {
      if (value) {
        setPasswordStrength(calculatePasswordStrength(value));
      } else {
        setPasswordStrength(null);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = true;
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = true;
      }
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = true;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = true;
    }

    setErrors(newErrors);
    const hasValidationErrors = Object.keys(newErrors).length > 0;
    setHasErrors(hasValidationErrors);
    return !hasValidationErrors;
  };

  const handleContinue = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'weak': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'strong': return 'text-blue-500';
      case 'very-strong': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getStrengthBgColor = (strength) => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-blue-500';
      case 'very-strong': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'weak': return t('signup.password.weak');
      case 'medium': return t('signup.password.medium');
      case 'strong': return t('signup.password.strong');
      case 'very-strong': return t('signup.password.veryStrong');
      default: return '';
    }
  };

  const getErrorMessage = () => {
    if (!formData.password && !formData.confirmPassword) {
      return t('signup.errors.allFieldsRequired');
    }
    
    if (!formData.password) {
      return t('signup.errors.allFieldsRequired');
    }
    
    if (formData.password && !validatePassword(formData.password).isValid) {
      return t('signup.errors.passwordRequirements');
    }
    
    if (!formData.confirmPassword) {
      return t('signup.errors.allFieldsRequired');
    }
    
    if (formData.password !== formData.confirmPassword) {
      return t('signup.errors.passwordMismatch');
    }
    
    return '';
  };

  const getDisplayMessage = () => {
    if (hasErrors) {
      return getErrorMessage();
    }
    
    return t('signup.password.requiredFieldsMessage');
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {t('signup.password.header')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('signup.password.instructions')}
        </p>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Password */}
        <div>
          <label htmlFor="password" className="labels block text-sm font-semibold mb-1">
            {t('signup.password.password')} *
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password || ''}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('signup.password.password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white border border-gray-300 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <BiHide size={20} /> : <BiShow size={20} />}
            </button>
          </div>
        </div>

        {/* Password Strength Indicator */}
        {passwordStrength && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('signup.password.passwordStrength')}</span>
              <span className={`font-medium capitalize ${getStrengthColor(passwordStrength.strength)}`}>
                {getStrengthText(passwordStrength.strength)}
              </span>
            </div>
            
            {/* Strength Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getStrengthBgColor(passwordStrength.strength)}`}
                style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
              />
            </div>

            {/* Requirements Checklist */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">{t('signup.password.passwordRequirements')}</p>
              <div className="space-y-1 text-xs">
                <div className={`flex items-center ${passwordStrength.checks.length ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.checks.length ? <BiCheck size={16} /> : <BiX size={16} />}
                  <span className="ml-1">{t('signup.password.characters')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.checks.uppercase ? <BiCheck size={16} /> : <BiX size={16} />}
                  <span className="ml-1">{t('signup.password.uppercase')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.checks.lowercase ? <BiCheck size={16} /> : <BiX size={16} />}
                  <span className="ml-1">{t('signup.password.lowercase')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.number ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.checks.number ? <BiCheck size={16} /> : <BiX size={16} />}
                  <span className="ml-1">{t('signup.password.number')}</span>
                </div>
                <div className={`flex items-center ${passwordStrength.checks.symbol ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.checks.symbol ? <BiCheck size={16} /> : <BiX size={16} />}
                  <span className="ml-1">{t('signup.password.symbol')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="labels block text-sm font-semibold mb-1">
            {t('signup.password.confirmPassword')} *
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword || ''}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('signup.password.confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white border border-gray-300 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <BiHide size={20} /> : <BiShow size={20} />}
            </button>
          </div>
          
          {/* Password Match Indicator - Only show if both fields have values */}
          {formData.password && formData.confirmPassword && (
            <div className={`flex items-center mt-1 text-xs ${
              formData.password === formData.confirmPassword ? 'text-green-600' : 'text-red-500'
            }`}>
              {formData.password === formData.confirmPassword ? <BiCheck size={16} /> : <BiX size={16} />}
              <span className="ml-1">
                {formData.password === formData.confirmPassword 
                  ? t('signup.password.passwordsMatch') 
                  : t('signup.password.passwordsDoNotMatch')
                }
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button
          variant="block"
          onClick={handleContinue}
          label={t('signup.password.continue')}
        />
        <button
          onClick={onBack}
          className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('signup.password.back')}
        </button>
      </div>

      {/* Helper Text - Show error message or default message */}
      <div className="mt-4 text-center">
        <p className={`text-xs ${hasErrors ? 'text-red-500' : 'text-gray-500'}`}>
          {getDisplayMessage()}
        </p>
      </div>
    </div>
  );
};

PasswordStep.propTypes = {
  formData: PropTypes.object.isRequired,
  onFormDataChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default PasswordStep;